// @flow
import * as React from 'react';
import { graphql } from 'gatsby';
import compose from 'recompose/compose';
import withStyles, {
  type WithStyles,
} from '@plugins/material-ui/hocs/withStyles';
import type { Theme } from '@material-ui/core/styles';

import NodeBody from '~plugins/prismic/components/NodeBody';
import useGroupedSlicesByType from '~plugins/prismic/hooks/useGroupedSlicesByType';
import withNodePage, {
  type WithNodePageProps,
} from '~plugins/prismic/hocs/withNodePage';
import Header from '~components/Header';
import { withSubThemeProvider } from '~components/SubThemeProvider';
import FooterContactForm from '~components/FooterContactForm';
import useSetAppBarState from '~components/AppBar/hooks/useSetAppBarState';
import type { PrismicSolutionPage } from '~schema';
import useAllNodeBodyComponents from '~hooks/useAllNodeBodyComponents';

import { SLICE_TABBED_CARD_GROUPED_NAME } from '../constants';

export type ClassKey = 'root' | 'bodySection';

export type Props = {
  ...$Exact<WithStyles<ClassKey>>,
  ...$Exact<WithNodePageProps<PrismicSolutionPage>>,
};

const styles = (unusedTheme: Theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    overflow: 'hidden',
  },
  bodySection: {},
});

const nodeBodyComponents = useAllNodeBodyComponents();

const SolutionPage = ({ classes, node }: Props) => {
  useSetAppBarState({
    overlayColor: node?.data?.overlay_color,
    backgroundImage: node?.data?.background_image,
  });

  const body = useGroupedSlicesByType({
    slices: node?.data?.body,
    sliceType: 'tabbed_cards',
    groupedType: SLICE_TABBED_CARD_GROUPED_NAME,
  });

  return (
    <div className={classes.root}>
      <Header
        componentVariant="simple"
        title={node?.data?.title}
        subtitle={node?.data?.subtitle}
        backgroundImage={node?.data?.background_image}
        overlayColor={node?.data?.overlay_color}
      />
      <NodeBody
        node={node}
        body={body}
        components={nodeBodyComponents}
        className={classes.bodySection}
      />
      <FooterContactForm currentPage={node?.data?.name?.text} />
    </div>
  );
};

export default compose(
  withNodePage<PrismicSolutionPage, *>({
    getNode: data => data?.prismicSolutionPage,
  }),
  withSubThemeProvider(({ node }, { lighten, darken }) => {
    const primaryColor = node?.data?.primary_color;
    return {
      colorPrimary: primaryColor
        ? {
            main: primaryColor,
            light: lighten(primaryColor, 0.7),
            dark: darken(primaryColor, 0.25),
          }
        : undefined,
    };
  }),
  withStyles<ClassKey, *, Props>(styles, { name: 'SolutionPage' }),
)(SolutionPage);

export const query = graphql`
  query SolutionPageQuery($prismicId: ID) {
    prismicSolutionPage(prismicId: { eq: $prismicId }) {
      id
      lang
      uid
      first_publication_date
      last_publication_date
      data {
        meta_title {
          text
        }
        meta_description {
          text
        }
        meta_keywords {
          meta_keyword {
            text
          }
        }
        meta_image {
          alt
          url
          dimensions {
            width
            height
          }
        }
        name {
          text
        }
        title {
          text
          html
        }
        subtitle {
          text
          html
        }
        primary_color
        background_image {
          alt
          fluid(maxWidth: 1200) {
            ...GatsbyPrismicImageFluid_withWebp_noBase64
          }
        }
        overlay_color
        body {
          ... on Node {
            id
          }
          ... on PrismicSolutionPageBodyTextWithImage {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_video {
                url
                link_type
              }
              slice_background_color
              overlapping_image
              image_position
            }
            items {
              item_highlighted_text {
                text
                html
              }
            }
          }
          ... on PrismicSolutionPageBodyItemsList {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 600) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
              overlapping_image
              image_position
            }
            items {
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 50, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyHighlightedItems {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 70, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicSolutionPageBodySingleImage {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 1000) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_caption {
                text
                html
              }
              image_width
            }
          }
          ... on PrismicSolutionPageBodyImageGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              image_width
              max_line_items
            }
            items {
              item_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              item_caption {
                text
                html
              }
            }
          }
          ... on PrismicSolutionPageBodyVideo {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_link {
                link_type
                url
              }
              video_width
              autoplay
              slice_image_placeholder {
                alt
                fluid(maxWidth: 1000) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyHighlightedContent {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
              image_position
            }
            items {
              item_title {
                text
                html
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyRelatedContent {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_link_label {
                text
              }
              slice_link {
                link_type
                target
                url
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyBanner {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_overlapping_image {
                alt
                fluid(maxWidth: 400) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_image {
                alt
                fluid(maxWidth: 800) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_image_color
              slice_background_color
            }
            items {
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_link_style
            }
          }
          ... on PrismicSolutionPageBodyDownloadForm {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_document {
                link_type
                url
                target
                document {
                  ... on PrismicWhitePaper {
                    id
                    data {
                      document_link {
                        url
                      }
                    }
                  }
                }
              }
              slice_background_color
              zoho_listkey
              zoho_lead_source
              zoho_industry
            }
          }
          ... on PrismicSolutionPageBodySolutions {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_link_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_background_image {
                alt
                fluid(maxWidth: 960) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyLinksGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_link_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
            }
          }
          ... on PrismicSolutionPageBodyTitleAndDescription {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_title_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
              container_width
            }
          }
          ... on PrismicSolutionPageBodyTabbedCards {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_tab_name {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
            }
            items {
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 50, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyArViewer {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_glb_model_link {
                link_type
                url
              }
              select_usdz_model_link {
                link_type
                url
              }
              slice_background_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
              viewer_button
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 50, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyPrice {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_price {
                text
              }
              slice_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_image_color
              slice_background_color
              overlapping_image
            }
            items {
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_link_style
            }
          }
          ... on PrismicSolutionPageBodyPrices {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_price {
                text
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyEmbeddedContent {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_embedded_content {
                text
                html
              }
            }
          }
          ... on PrismicSolutionPageBodyAnimation {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
            }
          }
          ... on PrismicSolutionPageBodyCallToAction {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_link_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_link_variant
            }
          }
          ... on PrismicSolutionPageBodyButtonCards {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
              }
              item_description {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 400) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyContentGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicSolutionPageBodyFloatingButton {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_label {
                text
              }
              slice_link {
                link_type
                target
                url
              }
            }
          }
          ... on PrismicSolutionPageBodyIconsGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              max_line_items
            }
            items {
              item_icon {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
            }
          }
          ... on PrismicSolutionPageBodyIconsCarousel {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
            }
            items {
              item_icon {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
            }
          }
        }
      }
    }
  }
`;
